<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import UiButton from '@/components/ui/UiButton.vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const ui = useUiStore()
const { theme } = storeToRefs(ui)
const { user } = storeToRefs(auth)
const { t } = useI18n()

const menuOpen = ref(false)
const signingOut = ref(false)

const title = computed(() => {
  const key = route.meta.titleKey as string | undefined
  return key ? t(key) : ((route.meta.title as string | undefined) ?? t('common.app'))
})
const appVersion = import.meta.env.VITE_APP_VERSION ?? ''

const initials = computed(() => {
  const name = user.value?.displayName ?? user.value?.email ?? '?'
  return name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
})

async function signOut(): Promise<void> {
  signingOut.value = true
  try {
    await auth.logout()
    await router.push({ name: 'login' })
  } finally {
    signingOut.value = false
    menuOpen.value = false
  }
}
</script>

<template>
  <header class="topbar">
    <button
      type="button"
      class="topbar__menu-toggle"
      :aria-label="t('nav.toggle')"
      @click="ui.sidebarOpenMobile = !ui.sidebarOpenMobile"
    >
      ☰
    </button>

    <h1 class="topbar__title">{{ title }}</h1>

    <div class="topbar__spacer" />

    <span v-if="appVersion" class="topbar__version mono" :title="t('nav.buildVersion')">
      v{{ appVersion }}
    </span>

    <UiButton
      size="sm"
      variant="ghost"
      :aria-label="t('common.switchLanguage')"
      :title="t('common.switchLanguage')"
      data-testid="language-toggle"
      @click="ui.toggleLocale()"
    >
      <span aria-hidden="true">◐</span>
      <span class="topbar__theme-label">{{ ui.locale === 'ar' ? t('common.english') : t('common.arabic') }}</span>
    </UiButton>

    <UiButton
      size="sm"
      variant="ghost"
      :aria-label="t('common.theme.switchTo', { theme: t(theme === 'dark' ? 'common.theme.light' : 'common.theme.dark') })"
      :title="t('common.theme.switchTo', { theme: t(theme === 'dark' ? 'common.theme.light' : 'common.theme.dark') })"
      @click="ui.toggleTheme()"
    >
      <span aria-hidden="true">{{ theme === 'dark' ? '☾' : '☀' }}</span>
      <span class="topbar__theme-label">{{ t(theme === 'dark' ? 'common.theme.dark' : 'common.theme.light') }}</span>
    </UiButton>

    <div class="topbar__account">
      <button
        type="button"
        class="topbar__avatar"
        :aria-expanded="menuOpen"
        aria-haspopup="menu"
        :aria-label="t('nav.accountMenu', { name: user?.displayName ?? '' })"
        @click="menuOpen = !menuOpen"
      >
        {{ initials }}
      </button>

      <div v-if="menuOpen" class="topbar__dropdown" role="menu">
        <div class="topbar__identity">
          <p class="topbar__name">{{ user?.displayName ?? '—' }}</p>
          <p class="topbar__email mono">{{ user?.email ?? '—' }}</p>
          <p class="topbar__role">
            {{ t('nav.role') }} <span class="mono">{{ user?.role ? t(`common.roles.${user.role}`) : '—' }}</span>
          </p>
        </div>
        <div class="topbar__permissions">
          <p class="topbar__permissions-label">{{ t('nav.permissions') }}</p>
          <ul>
            <li v-for="permission in user?.permissions ?? []" :key="permission" class="mono">
              {{ permission }}
            </li>
            <li v-if="(user?.permissions ?? []).length === 0" class="dim">{{ t('nav.noPermissions') }}</li>
          </ul>
        </div>
        <RouterLink class="topbar__link" :to="{ name: 'account' }" @click="menuOpen = false">
          {{ t('nav.changePassword') }}
        </RouterLink>
        <UiButton variant="ghost" size="sm" block :loading="signingOut" @click="signOut">
          {{ t('common.actions.signOut') }}
        </UiButton>
      </div>
    </div>
  </header>
</template>

<style scoped>
.topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  height: var(--topbar-height);
  padding: 0 var(--space-5);
  background: var(--surface-1);
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.topbar__link {
  display: block;
  padding: var(--space-2) var(--space-3);
  color: var(--text-primary);
  text-decoration: none;
  border-radius: var(--radius);
}

.topbar__link:hover,
.topbar__link:focus-visible {
  background: var(--surface-2);
}

.topbar__menu-toggle {
  display: none;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--text-secondary);
  border-radius: var(--radius);
  width: 30px;
  height: 30px;
  cursor: pointer;
}

.topbar__title {
  font-size: var(--text-md);
  font-weight: 600;
  letter-spacing: -0.01em;
}

.topbar__spacer {
  flex: 1;
}

.topbar__version {
  font-size: var(--text-xs);
  color: var(--text-muted);
  padding: 2px var(--space-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
}

.topbar__theme-label {
  font-size: var(--text-xs);
}

.topbar__account {
  position: relative;
}

.topbar__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid var(--accent-border);
  background: var(--accent-soft);
  color: var(--accent);
  font-size: var(--text-xs);
  font-weight: 700;
  cursor: pointer;
}

.topbar__dropdown {
  position: absolute;
  inset-inline-end: 0;
  top: calc(100% + var(--space-2));
  width: 260px;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-modal);
}

.topbar__name {
  font-size: var(--text-sm);
  font-weight: 600;
}

.topbar__email,
.topbar__role {
  font-size: var(--text-xs);
  color: var(--text-secondary);
  overflow-wrap: anywhere;
}

.topbar__permissions {
  border-top: 1px solid var(--border-subtle);
  padding-top: var(--space-2);
}

.topbar__permissions-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  margin-bottom: var(--space-1);
}

.topbar__permissions li {
  font-size: var(--text-xs);
  color: var(--text-secondary);
}

.mono {
  font-family: var(--font-mono);
}

@media (max-width: 1024px) {
  .topbar__menu-toggle {
    display: block;
  }
}
</style>
